import {
  CalendarDots,
  ChatsCircle,
  Check,
  DotsThreeVertical,
  House,
  PencilSimple,
  Plus,
  SignOut,
  UserMinus,
  UsersThree,
  X,
} from '@phosphor-icons/react';
import type { AssociationDuServeur, Post, PublicUser, SiroccoEvent } from '@sirocco/shared';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { associations as associationsApi, events as eventsApi, posts as postsApi } from '../../api/endpoints';
import { EventCard } from '../../components/EventCard';
import { PostCard } from '../../components/PostCard';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/Bits';
import { Button, IconButton } from '../../components/ui/Button';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { profilePath } from '../../utils/routes';
import { t } from '../../i18n';
import { fondImage, MaPlace } from './AssociationsScreen';
import { useToast } from './commun';

type Onglet = 'fil' | 'evenements' | 'membres';

/**
 * La page d'une association : son fil, ses evenements, ses membres.
 *
 * Elle se tient comme un spot en petit : sa banniere en haut, bord a
 * bord, ses onglets en icones, le crayon du fil et « Creer » des
 * evenements pour ses membres — on est dans les menus principaux, mais
 * dans l'association.
 *
 * Toute l'ecole la lit. Pour y ecrire ou y creer un evenement, il faut en
 * etre : on demande a la rejoindre, et son chef accepte. Le chef voit en
 * plus les demandes en attente, et peut retirer un membre.
 */
export function AssociationScreen() {
  const { asso = '' } = useParams<{ asso: string }>();
  const { user, server } = useSession();
  const navigate = useNavigate();
  const textes = t.messages.annuaire.associations;
  const [onglet, setOnglet] = useState<Onglet>('fil');
  const [occupe, setOccupe] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuAnchor = useRef<HTMLButtonElement>(null);
  const [toast, flash] = useToast();
  const { pending, request, cancel, confirm } = useConfirm();

  const page = useAsync(() => associationsApi.page(asso), [asso, server?.id], `association-${server?.id}-${asso}`);
  const fil = useAsync(() => associationsApi.fil(asso), [asso, server?.id], `association-fil-${server?.id}-${asso}`);
  const sorties = useAsync(() => eventsApi.list(), [server?.id], `association-sorties-${server?.id}`);

  const data = page.data;
  const association = data?.association;
  const membre = association?.monRole === 'chef' || association?.monRole === 'membre';
  const chef = association?.monRole === 'chef';
  const aUnChef = Boolean(data?.membres.some((ligne) => ligne.role === 'chef'));
  const evenements = (sorties.data ?? []).filter((sortie: SiroccoEvent) => sortie.asso === asso && !sortie.isPast);

  /* Un geste, puis la page se relit : le compte des membres et les demandes suivent. */
  async function agir(geste: () => Promise<void>, apres?: string) {
    setOccupe(true);
    try {
      await geste();
      await page.refresh();
      if (apres) flash(apres);
    } catch {
      flash(textes.impossible);
    } finally {
      setOccupe(false);
    }
  }

  async function ouvrirLeGroupe() {
    setOccupe(true);
    try {
      navigate(`/app/messages/${await associationsApi.groupe(asso)}`);
    } catch (cause) {
      flash(cause instanceof Error ? cause.message : textes.impossible);
    } finally {
      setOccupe(false);
    }
  }

  function remplacer(post: Post) {
    fil.setData((avant) => avant.map((ancien) => (ancien.id === post.id ? post : ancien)));
  }

  async function aimer(post: Post) {
    remplacer({ ...post, liked: !post.liked, likeCount: post.likeCount + (post.liked ? -1 : 1) });
    try {
      remplacer(await postsApi.like(post));
    } catch {
      void fil.reload();
    }
  }

  async function garder(post: Post) {
    remplacer({ ...post, bookmarked: !post.bookmarked, bookmarkCount: post.bookmarkCount + (post.bookmarked ? -1 : 1) });
    try {
      remplacer(await postsApi.bookmark(post));
    } catch {
      void fil.reload();
    }
  }

  const nombreDeDemandes = chef ? data?.demandes.length ?? 0 : 0;
  const onglets: { valeur: Onglet; libelle: string; Icone: typeof House }[] = [
    { valeur: 'fil', libelle: textes.fil, Icone: House },
    { valeur: 'evenements', libelle: textes.evenements, Icone: CalendarDots },
    { valeur: 'membres', libelle: textes.lesMembres, Icone: UsersThree },
  ];

  return (
    <SubScreen
      title={association?.nom ?? textes.titre}
      hero={association ? <Entete association={association} /> : undefined}
      onRefresh={async () => {
        await Promise.all([page.refresh(), fil.refresh(), sorties.refresh()]);
      }}
      roue
      action={
        membre ? (
          <IconButton label={t.sorties.commun.autresActions} variant="ghost" size={34} ref={menuAnchor} onClick={() => setMenu(true)}>
            <DotsThreeVertical size={22} weight="bold" />
          </IconButton>
        ) : undefined
      }
      overlay={
        <>
          {toast && <p className="feed__toast">{toast}</p>}
          <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />
          <Sheet open={menu} onClose={() => setMenu(false)} anchor={menuAnchor}>
            <SheetAction
              icon={<SignOut className="sens-x" size={20} />}
              tone="danger"
              onClick={() => {
                setMenu(false);
                request(textes.quitterQ(association?.nom ?? ''), () =>
                  void agir(() => associationsApi.quitter(asso)),
                );
              }}
            >
              {textes.quitter}
            </SheetAction>
          </Sheet>
          {/* Le crayon du fil, comme sur le fil du spot : pour ses membres seuls. */}
          {membre && onglet === 'fil' && (
            <button className="fab as-fab" aria-label={textes.ecrire} onClick={() => navigate(`/app/feed/compose?asso=${asso}`)}>
              <PencilSimple size={26} weight="regular" />
            </button>
          )}
        </>
      }
    >
      {page.loading && !data && <ScreenLoader />}
      {page.error && <ScreenError message={page.error} onRetry={page.reload} />}

      {data && association && (
        <div className="as-page">
          <header className="as-tete">
            <h2 className="as-tete__nom">
              <bdi>{association.nom}</bdi>
            </h2>
            <p className="as-tete__meta">
              <UsersThree size={16} />
              {textes.membres(association.membres)}
              <MaPlace asso={association} />
            </p>
            {association.description && <p className="as-tete__description">{association.description}</p>}

            {/* Qui n'en est pas demande a entrer, ou attend la reponse du chef. */}
            {!membre &&
              (association.maDemande ? (
                <Button
                  block
                  variant="soft"
                  loading={occupe}
                  onClick={() => void agir(() => associationsApi.annulerDemande(asso))}
                >
                  {textes.enAttente} · {textes.annulerDemande}
                </Button>
              ) : (
                <Button
                  block
                  loading={occupe}
                  onClick={() => void agir(() => associationsApi.demander(asso), textes.demandeEnvoyee)}
                >
                  {textes.rejoindre}
                </Button>
              ))}
            {!membre && <p className="as-tete__note">{aUnChef ? textes.reserveMembres : textes.sansChef}</p>}
          </header>

          {/*
            Les onglets de l'association, comme ceux du spot : une icone et
            son nom, l'icone pleine pour l'onglet ou l'on est.
          */}
          <nav className="as-onglets" role="tablist" aria-label={association.nom}>
            {onglets.map(({ valeur, libelle, Icone }) => {
              const actif = onglet === valeur;
              return (
                <button
                  key={valeur}
                  type="button"
                  role="tab"
                  aria-selected={actif}
                  className={`as-onglet${actif ? ' as-onglet--actif' : ''}`}
                  onClick={() => setOnglet(valeur)}
                >
                  <span className="as-onglet__icone">
                    <Icone size={24} weight={actif ? 'fill' : 'regular'} />
                    {valeur === 'membres' && nombreDeDemandes > 0 && (
                      <span className="as-onglet__pastille">{nombreDeDemandes}</span>
                    )}
                  </span>
                  {libelle}
                </button>
              );
            })}
          </nav>

          {onglet === 'fil' && (
            <div className="as-fil">
              {fil.loading && !fil.data && <ScreenLoader />}
              {fil.data && fil.data.length === 0 && <EmptyState title={textes.filVide} />}
              {(fil.data ?? []).map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  sansAsso
                  onLike={aimer}
                  onBookmark={garder}
                  onOpenComments={(choisi, ecrire) =>
                    navigate(`/app/feed/${choisi.id}/comments`, { state: { post: choisi, compose: ecrire } })
                  }
                />
              ))}
            </div>
          )}

          {onglet === 'evenements' && (
            <div className="as-evenements">
              {/* Creer, comme sur l'onglet des evenements : pour ses membres seuls. */}
              {membre && (
                <Link to={`/app/events/new?asso=${asso}`} className="ev-create as-creer">
                  <Plus size={16} weight="bold" />
                  {textes.creerEvenement}
                </Link>
              )}
              {sorties.loading && !sorties.data && <ScreenLoader />}
              {sorties.data && evenements.length === 0 && <EmptyState title={textes.evenementsVide} />}
              {evenements.map((sortie) => (
                <EventCard key={sortie.id} event={sortie} />
              ))}
            </div>
          )}

          {onglet === 'membres' && (
            <div className="as-membres">
              {/* Le groupe ou tous les membres sont entres ; qui l'a quitte y revient d'ici. */}
              {membre && (
                <Button
                  block
                  variant="soft"
                  icon={<ChatsCircle size={18} />}
                  loading={occupe}
                  onClick={() => void ouvrirLeGroupe()}
                >
                  {textes.groupe}
                </Button>
              )}
              {chef && data.demandes.length > 0 && (
                <section>
                  <h3 className="as-section">{textes.demandes(data.demandes.length)}</h3>
                  {data.demandes.map((demandeur) => (
                    <Ligne key={demandeur.id} personne={demandeur} moi={user?.id}>
                      <IconButton
                        label={textes.refuser(demandeur.username)}
                        variant="soft"
                        size={36}
                        disabled={occupe}
                        onClick={() => void agir(() => associationsApi.refuser(asso, demandeur.id))}
                      >
                        <X size={18} weight="bold" />
                      </IconButton>
                      <IconButton
                        label={textes.accepter(demandeur.username)}
                        size={36}
                        disabled={occupe}
                        onClick={() => void agir(() => associationsApi.accepter(asso, demandeur.id))}
                      >
                        <Check size={18} weight="bold" />
                      </IconButton>
                    </Ligne>
                  ))}
                </section>
              )}
              <section>
                {chef && data.demandes.length > 0 && <h3 className="as-section">{textes.lesMembres}</h3>}
                {data.membres.map(({ user: personne, role }) => (
                  <Ligne key={personne.id} personne={personne} moi={user?.id} role={role}>
                    {chef && role === 'membre' && personne.id !== user?.id && (
                      <IconButton
                        label={textes.retirer(personne.username)}
                        variant="ghost"
                        size={36}
                        disabled={occupe}
                        onClick={() =>
                          request(textes.retirerQ(personne.username), () =>
                            void agir(() => associationsApi.retirer(asso, personne.id)),
                          )
                        }
                      >
                        <UserMinus size={18} />
                      </IconButton>
                    )}
                  </Ligne>
                ))}
              </section>
            </div>
          )}
        </div>
      )}
    </SubScreen>
  );
}

/**
 * Le haut de la page : la banniere de l'association, bord a bord, comme
 * l'illustration d'un spot. Sans banniere, la couleur du spot et
 * l'initiale de l'association.
 */
function Entete({ association }: { association: AssociationDuServeur }) {
  if (association.banniere) {
    return <div className="as-entete" style={fondImage(association.banniere)} aria-hidden="true" />;
  }
  return (
    <div className="as-entete as-entete--sans" aria-hidden="true">
      <span className="as-entete__initiale">{association.nom.charAt(0).toUpperCase()}</span>
    </div>
  );
}

/** Une personne de l'association : son visage et son pseudo menent a son profil. */
function Ligne({
  personne,
  moi,
  role,
  children,
}: {
  personne: PublicUser;
  moi?: string;
  role?: 'chef' | 'membre';
  children?: React.ReactNode;
}) {
  return (
    <div className="card ms-person as-ligne">
      <Link to={profilePath(personne.id, moi)} className="ms-person__open">
        <Avatar user={personne} size={40} />
        <span className="ms-person__body">
          <span className="ms-person__name">{personne.username}</span>
          {role === 'chef' && <span className="as-place as-place--chef">{t.messages.annuaire.associations.chef}</span>}
        </span>
      </Link>
      {children && <span className="as-ligne__gestes">{children}</span>}
    </div>
  );
}
